import { useMutation, useQuery } from '@apollo/client'
import { captureException } from '@sentry/react'
import { useCallback, useMemo, useState } from 'react'

import openNotification from 'src/components/common/notification'
import { INSERT_DEFAULT_SIMULATION } from 'src/schema/mutations'
import { GET_SIMULATION_SESSION } from 'src/schema/queries'
import { handlePromise } from 'src/utils/handlePromise'
import useORNAApolloClient from 'src/utils/hooks/useORNAApolloClient'

import logger from '../logger'

/**
 * State to control simulation mode
 * @param {string} authToken
 * @param {string} organizationId
 * @returns {{isSimulationEnabled: boolean, setIsSimulationEnabled: (enabled:boolean) => void, simulationId:string }}
 */
const useSimulation = (authToken, organizationId) => {
    /**
     * @type {ReturnType<typeof useState<boolean>>}
     */
    const [isSimulationEnabled, setIsSimulationEnabled] = useState(false)

    /**
     * @type {ReturnType<typeof useState<{id:string, created_at: number, updated_at:number}>>}
     */
    const [session, setSession] = useState(null)

    const client = useORNAApolloClient({
        accessToken: authToken,
        organizationId,
    })

    const [insertDefaultSimulation] = useMutation(INSERT_DEFAULT_SIMULATION, {
        client,
    })

    const createDefaultSimulation = useCallback(async () => {
        const [error, queryResult] = await handlePromise(
            insertDefaultSimulation({ variables: {} })
        )

        if (error) {
            setIsSimulationEnabled(false)
            const errMessage =
                'An error occurred while setting up your simulation environment, please try reloading this page'
            openNotification('error', errMessage, 3)
            captureException(error, {
                extra: {
                    content: errMessage,
                },
            })
            return
        }

        setSession(queryResult.data.inserted_row)
    }, [insertDefaultSimulation])

    const skipSimulationQuery =
        !authToken || !organizationId || !isSimulationEnabled

    useQuery(GET_SIMULATION_SESSION, {
        skip: skipSimulationQuery,
        client,
        onError: (error) => {
            const errMessage =
                'An error occurred while fetching simulation session data\n' +
                error.message
            const err = new Error(err)
            captureException(err, {
                extra: {
                    content: errMessage,
                },
            })
            openNotification(
                'error',
                'An error occurred while setting up your simulation environment, please try reloading this page',
                3
            )
            logger.error(err)
            return
        },
        onCompleted: async (data) => {
            /**@type {{id:string, created_at: number, updated_at: number}[]} */
            const sessions = data?.sessions ?? null

            if (sessions.length < 1) {
                await createDefaultSimulation()

                /**
                 * NOTE:
                 * this callback prematurely terminates operation whenever a new error is created
                 * in this scope. this can cause the following unexpected problems
                 * 1. calling setState after 'new Error' -> the state update will not be applied
                 * as the function terminates after reaching 'new Error'. Also applies to other stuff
                 * such
                 * as console.log/logger.error
                 */

                return
            }

            setSession(sessions[0])
        },
    })

    const metadata = useMemo(() => {
        return {
            simulationId: session?.id ?? null,
            isSimulationEnabled: isSimulationEnabled,
            setIsSimulationEnabled,
        }
    }, [isSimulationEnabled, session?.id])

    return metadata
}

export default useSimulation
